/**
 * Project finance — pure formulas (FA pack, FA11 per task split).
 *
 * Computes LCOE, NPV, project IRR, and simple payback from a
 * `FinancialAssumptions` record. Pure functions only — no LLM, no
 * UI, no store side-effects. The form and the sensitivity panel call
 * `recompute(fa)` on every input change.
 *
 * UNIT DISCIPLINE (#1 regression source on this codebase):
 *   - capacityMW                 ⇒ MW       (multiply by 1000 ⇒ kW)
 *   - capacityFactorPct          ⇒ percent  (divide by 100 ⇒ fraction)
 *   - projectLifeYears           ⇒ years
 *   - capexTotalUsd              ⇒ USD (NOT $k, NOT $M)
 *   - fixedOmPerKwYearUsd        ⇒ USD per kW per year
 *   - variableOmPerMwhUsd        ⇒ USD per MWh
 *   - fuelCostPerMwhUsd          ⇒ USD per MWh
 *   - ppaPricePerMwhUsd          ⇒ USD per MWh
 *   - waccPct / *Pct fields      ⇒ percent (divide by 100 ⇒ fraction)
 *   - LCOE returned              ⇒ USD per MWh (NOT $/kWh)
 *
 * Annual energy:
 *   annualMwh = capacityMW × 8760 × (capacityFactorPct / 100)
 *
 * Hours-per-year is 8760 (8784 for leap years is ignored — the LCOE
 * formula is steady-state).
 */
import {
  isWcDecommCashflowsEnabled,
  resolveWcDecommUsd,
  setWcDecommCashflowsForTests,
} from '../config/financeFlags';
import {
  DEFAULT_INFLATION_PCT_PER_YEAR,
  fuelEscalatorPctPerYear,
  realDiscountRateFraction,
  realEscalatorFraction,
} from './inflationBasis';
import type {
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialField,
  FieldProvenance,
} from '../types/financialAssumptions';
import type { BomItem } from '../types/supplyChain';
import { bomItemDepositUsd } from '../types/supplyChain';

/** Hours per year used in capacity-factor → energy conversions. */
export const HOURS_PER_YEAR = 8760;

/** kW per MW — the constant that bridges $/kW and $/MW. */
export const KW_PER_MW = 1000;

/**
 * R-131-FIN-DECISION-C / R-131-SCORING-A / H-5 (FA_WC_DECOMM_CASHFLOWS) — when
 * enabled, working-capital lock-up (year 0) and recovery + decommissioning
 * (year N) flow through legacy cashflow / LCOE helpers. The CODE default is off
 * for backwards compatibility, but the flag is **ON in production** (see
 * `financeFlags.ts`) — every persisted LCOE/NPV includes these terms.
 *
 * The flag + its sourced default assumptions now live in
 * `src/config/financeFlags.ts` (H-5: centralised, env-plumbed, no magic
 * numbers). These thin wrappers preserve the legacy names so existing callers /
 * tests are byte-identical.
 */

/** Test-only override for WC + decommissioning cashflow inclusion. */
export function setIncludeWcAndDecommissioningForTests(value: boolean | undefined): void {
  setWcDecommCashflowsForTests(value);
}

export function isIncludeWcAndDecommissioningEnabled(): boolean {
  return isWcDecommCashflowsEnabled();
}

// ─────────────────────────────────────────────────────────────────────────────
// CURRENCY BOUNDARY.
//
// Every formula in this module computes in USD. The schema has always carried
// `currency` + `exchangeRateToUsd` and documented that "project-finance
// formulas always operate in USD after the boundary normalisation in
// `recompute()`" — but no such normalisation existed anywhere in the codebase:
// `exchangeRateToUsd` was read only by the report authority gate, as a presence
// check. A model entered in EUR was computed as though it were USD.
//
// This is the boundary. It is applied by `canonicalCore` /
// `canonicalFinancialAssumptions`, so every engine consumer receives USD.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Monetary fields on `FinancialAssumptionsCore`, in the currency the record
 * declares. Per-energy and per-kW rates are money too — a €/MWh price is not a
 * $/MWh price. Percentages, capacities, dates and durations are NOT converted.
 */
const MONETARY_CORE_FIELDS = [
  'capexTotalUsd',
  'capexPerKwUsd',
  'decommissioningUsd',
  'workingCapitalUsd',
  'fixedOmPerKwYearUsd',
  'variableOmPerMwhUsd',
  'fuelCostPerMwhUsd',
  'ptcPerMwhUsd',
  'ppaPricePerMwhUsd',
  'lcoeNominalPerMwhUsd',
  'lcoeRealPerMwhUsd',
  'npvUsd',
] as const;

/**
 * Convert a core's monetary fields into USD.
 *
 * `exchangeRateToUsd` is USD per unit of the declared currency (the schema's
 * convention), so USD = local × rate. A rate of 1, a missing rate, or a USD
 * record returns the input UNCHANGED — this is a no-op for the ~all-USD estate,
 * so no existing number moves.
 *
 * NOTE the `*Usd` suffixes stay: the field names describe the canonical unit
 * the engine requires, and renaming ~12 fields across the codebase to express
 * "whatever currency the record declares" would be a far larger change than the
 * bug warrants. The currency lives on the record, not in the field name.
 */
export function convertCoreToUsd(
  core: FinancialAssumptionsCore,
  exchangeRateToUsd: number | null | undefined,
): FinancialAssumptionsCore {
  if (
    exchangeRateToUsd == null
    || !Number.isFinite(exchangeRateToUsd)
    || exchangeRateToUsd <= 0
    || exchangeRateToUsd === 1
  ) {
    return core;
  }
  // Build the converted values as a typed patch — every entry in
  // MONETARY_CORE_FIELDS is `number | null` on the core, so no cast is needed.
  const converted: Partial<Record<(typeof MONETARY_CORE_FIELDS)[number], number>> = {};
  for (const field of MONETARY_CORE_FIELDS) {
    const v = core[field];
    if (typeof v === 'number' && Number.isFinite(v)) converted[field] = v * exchangeRateToUsd;
  }
  const out: FinancialAssumptionsCore = { ...core, ...converted };
  const land = out.landCost?.totalUsd;
  if (out.landCost && typeof land === 'number' && Number.isFinite(land)) {
    out.landCost = {
      ...out.landCost,
      totalUsd: land * exchangeRateToUsd,
      ...(out.landCost.perParcel
        ? {
          perParcel: out.landCost.perParcel.map((p) => ({
            ...p,
            usdPerAcre: p.usdPerAcre * exchangeRateToUsd,
            totalUsd: p.totalUsd * exchangeRateToUsd,
          })),
        }
        : {}),
    };
  }
  return out;
}

/**
 * Annual electricity production in MWh.
 *
 *   annualMwh = capacityMW × hours × CF
 *             [MW × hr × dimensionless]
 *             = MWh
 *
 * Returns `null` when any input is missing or non-physical.
 */
export function computeAnnualMwh(
  capacityMW: number | null,
  capacityFactorPct: number | null,
): number | null {
  if (capacityMW == null || capacityFactorPct == null) return null;
  if (capacityMW <= 0 || capacityFactorPct <= 0 || capacityFactorPct > 100) return null;
  return capacityMW * HOURS_PER_YEAR * (capacityFactorPct / 100);
}

/**
 * Are the inputs PHYSICALLY MEANINGFUL? A metric computed from an impossible
 * input is not a conservative estimate — it is a number with no meaning, and
 * these ones do not even fail loudly:
 *
 *   waccPct ≤ −100  ⇒ `(1 + r)` ≤ 0. NPV returns Infinity (measured: 4e26 at
 *                     −150%), and CRF's own guard returns 0, which silently
 *                     DROPS the entire CAPEX term from the LCOE.
 *   projectLifeYears ≤ 0 ⇒ CRF 0 ⇒ LCOE is opex-only.
 *   capexTotalUsd < 0    ⇒ negative LCOE.
 *   non-integer life     ⇒ the cashflow loop truncates to whole years while CRF
 *                          uses the fraction, so the two disagree. Reachable
 *                          from the sensitivity panel's ±20% life swing
 *                          (33 × 1.2 = 39.6).
 *
 * Returning null puts these on the same footing as a missing input: the report
 * says it cannot compute, instead of publishing a confident absurdity.
 */
export function hasPhysicalInputs(a: FinancialAssumptionsCore): boolean {
  if (a.waccPct != null && (!Number.isFinite(a.waccPct) || a.waccPct <= -100)) return false;
  if (a.projectLifeYears != null
    && (!Number.isFinite(a.projectLifeYears) || a.projectLifeYears < 1)) return false;
  if (a.capexTotalUsd != null && (!Number.isFinite(a.capexTotalUsd) || a.capexTotalUsd < 0)) return false;
  if (a.capacityMW != null && (!Number.isFinite(a.capacityMW) || a.capacityMW < 0)) return false;
  return true;
}

/**
 * Project life as a WHOLE number of years — the cashflow vector is annual, so a
 * fractional life would discount a year the vector does not contain. Rounded
 * (not truncated) so a ±20% swing on 33 years reads as 40, not 39.
 */
export function effectiveProjectLifeYears(a: FinancialAssumptionsCore): number | null {
  if (a.projectLifeYears == null || !Number.isFinite(a.projectLifeYears)) return null;
  const rounded = Math.round(a.projectLifeYears);
  return rounded >= 1 ? rounded : null;
}

/**
 * Capital Recovery Factor — annualises a CAPEX into a level annual
 * payment over the project life at a given discount rate.
 *
 *   CRF(r, n) = r / (1 - (1 + r)^-n)
 *
 * Edge case: when `r` is effectively zero, CRF degenerates to `1/n`
 * (per L'Hôpital). Guarded so callers don't divide by zero on
 * unfunded projects.
 */
/**
 * B14 — the factor by which a LEVEL annual cost must be multiplied to levelise
 * an ESCALATING one over the same life:
 *
 *   CRF(r,N) × Σ_{t=1..N} (1+g)^(t-1) / (1+r)^t
 *
 * At g = 0 this is mathematically exactly 1, but computing it in floating point
 * yields 1 ± 1e-16, which would perturb every persisted LCOE by a hair. The
 * zero short-circuit is therefore load-bearing, not an optimisation: it is what
 * makes the flag-off path byte-identical.
 */
export function opexLevelisationFactor(
  discountRateFraction: number,
  escalatorFraction: number,
  lifeYears: number,
): number {
  if (escalatorFraction === 0) return 1;
  if (lifeYears <= 0) return 0;
  let pv = 0;
  for (let t = 1; t <= lifeYears; t += 1) {
    pv += Math.pow(1 + escalatorFraction, t - 1) / Math.pow(1 + discountRateFraction, t);
  }
  return capitalRecoveryFactor(discountRateFraction, lifeYears) * pv;
}

export function capitalRecoveryFactor(discountRateFraction: number, lifeYears: number): number {
  if (lifeYears <= 0) return 0;
  if (Math.abs(discountRateFraction) < 1e-9) return 1 / lifeYears;
  const denom = 1 - Math.pow(1 + discountRateFraction, -lifeYears);
  // denom can hit ~0 for negative rates near -100% — guard to avoid Infinity.
  if (Math.abs(denom) < 1e-12) return 0;
  return discountRateFraction / denom;
}

/**
 * Annual OPEX in USD/year:
 *   fixed-O&M ($/kW/yr × kW)
 *   + variable-O&M ($/MWh × MWh/yr)
 *   + fuel-cost  ($/MWh × MWh/yr)
 *
 * Returns null when any of fixed-O&M / capacity / capacity-factor is
 * missing (variable and fuel default to 0 since not all techs carry
 * them — solar has no fuel cost; SMR has minimal variable O&M).
 */
/**
 * The FUEL component of annual opex, USD/yr. Split out because fuel does not
 * escalate with general inflation — gas tracks a commodity path (EIA AEO),
 * everything else is held flat in real terms. See `fuelEscalatorPctPerYear`.
 */
export function computeAnnualFuelUsd(a: FinancialAssumptionsCore): number | null {
  if (a.capacityMW == null) return null;
  const annualMwh = computeAnnualMwh(a.capacityMW, a.capacityFactorPct);
  if (annualMwh == null) return null;
  return (a.fuelCostPerMwhUsd ?? 0) * annualMwh;
}

/** Annual opex EXCLUDING fuel — the part that escalates with general inflation. */
export function computeAnnualNonFuelOpexUsd(a: FinancialAssumptionsCore): number | null {
  const total = computeAnnualOpexUsd(a);
  const fuel = computeAnnualFuelUsd(a);
  if (total == null || fuel == null) return null;
  return total - fuel;
}

export function computeAnnualOpexUsd(a: FinancialAssumptionsCore): number | null {
  if (a.fixedOmPerKwYearUsd == null || a.capacityMW == null) return null;
  const annualMwh = computeAnnualMwh(a.capacityMW, a.capacityFactorPct);
  if (annualMwh == null) return null;
  const fixed = a.fixedOmPerKwYearUsd * a.capacityMW * KW_PER_MW;
  const variable = (a.variableOmPerMwhUsd ?? 0) * annualMwh;
  const fuel = (a.fuelCostPerMwhUsd ?? 0) * annualMwh;
  // Insurance + property tax is quoted as an annual PERCENT OF CAPITAL, the
  // industry convention for both lines. It was an inert form field until
  // 2026-08-19: declaring 1.5%/yr moved no number anywhere. Absent ⇒ zero.
  return fixed + variable + fuel + computeAnnualInsuranceAndTaxUsd(a);
}

/**
 * Annual insurance + property tax in USD/yr — `insuranceAndPropertyTaxPct` of
 * the effective capital. Zero when the percentage or the CAPEX is absent.
 */
export function computeAnnualInsuranceAndTaxUsd(a: FinancialAssumptionsCore): number {
  const pct = a.insuranceAndPropertyTaxPct;
  if (pct == null || !Number.isFinite(pct) || pct <= 0) return 0;
  const capex = computeEffectiveCapexUsd(a);
  if (capex == null || capex <= 0) return 0;
  return capex * (pct / 100);
}

// ─────────────────────────────────────────────────────────────────────────────
// EFFECTIVE CAPEX — the capital the project actually has to raise.
//
// Review 2026-08-19: `contingencyPct`, `landCost` and `constructionMonths` were
// offered as inputs on the form, printed into the LLM prompt as authoritative,
// and consumed by NOTHING. Entering a 15% contingency moved no number in any
// report. These helpers close that gap; every component is OPTIONAL and absent
// components contribute exactly zero, so a model that declares none is
// unchanged.
// ─────────────────────────────────────────────────────────────────────────────

/** Months in a year — named per CLAUDE.md §4 (no magic numbers). */
const MONTHS_PER_YEAR_FIN = 12;

/**
 * Interest During Construction (IDC) — the financing cost of capital that is
 * spent before the plant earns anything.
 *
 *   IDC ≈ base_capex × r_debt × (construction_years / 2)
 *
 * The ÷2 is the standard mid-point approximation: spend accrues roughly
 * linearly over the build, so the AVERAGE outstanding balance is about half the
 * total for about the whole period. It is an approximation, not a drawdown
 * schedule — when a BoM with `costTiming` is available the cashflow builder
 * models the real drawdown instead and this term is not used.
 *
 * ASSUMPTION (stated, per CLAUDE.md §5): construction is financed at
 * `debtRatePct`. With no debt rate declared there is no defensible financing
 * cost to impute, so IDC is ZERO rather than guessed.
 */
export function computeIdcUsd(a: FinancialAssumptionsCore, baseCapexUsd: number): number {
  if (a.constructionMonths == null || !Number.isFinite(a.constructionMonths) || a.constructionMonths <= 0) return 0;
  if (a.debtRatePct == null || !Number.isFinite(a.debtRatePct) || a.debtRatePct <= 0) return 0;
  const constructionYears = a.constructionMonths / MONTHS_PER_YEAR_FIN;
  return baseCapexUsd * (a.debtRatePct / 100) * (constructionYears / 2);
}

/**
 * Total capital to raise, in USD:
 *
 *   (capexTotalUsd + landCost) × (1 + contingency) + IDC
 *
 * LAND: `landCost.totalUsd` is ADDITIVE. The schema documents that an absent
 * `landCost` means "land is rolled into `capexTotalUsd`" — so a PRESENT one is
 * a separate component and adding it is the documented reading.
 *
 * CONTINGENCY applies to the construction scope INCLUDING land, then IDC is
 * financed on that risked total. Returns null when there is no CAPEX at all.
 */
export function computeEffectiveCapexUsd(a: FinancialAssumptionsCore): number | null {
  if (a.capexTotalUsd == null || !Number.isFinite(a.capexTotalUsd)) return null;
  const land = a.landCost?.totalUsd;
  const withLand = a.capexTotalUsd + (land != null && Number.isFinite(land) && land > 0 ? land : 0);
  const contingencyPct = a.contingencyPct;
  const risked = contingencyPct != null && Number.isFinite(contingencyPct) && contingencyPct > 0
    ? withLand * (1 + contingencyPct / 100)
    : withLand;
  return risked + computeIdcUsd(a, risked);
}

/**
 * Levelized Cost of Energy in USD/MWh.
 *
 *   LCOE = (CAPEX × CRF + annual_OPEX) / annual_MWh
 *
 * Returns null when any required input is missing. This is the
 * simple-form LCOE — does not account for depreciation tax shield,
 * ITC/PTC, debt amortisation schedule, residual value, or
 * inflation-vs-nominal split. The form's "Output metrics" section
 * compares this against an uploaded model's LCOE; >5% diff triggers
 * a verification chip.
 */
/**
 * The shared LCOE kernel. `rateOverrideFraction` and `opexEscalatorFraction`
 * are the ONLY things that differ between the nominal and real figures — see
 * `computeLcoeRealUsdPerMwh` for why that is the whole point.
 */
function lcoeCore(
  a: FinancialAssumptionsCore,
  rateOverrideFraction: number | null,
  opexEscalatorFraction: number,
  fuelEscalatorFraction: number,
): number | null {
  if (
    a.capexTotalUsd == null
    || a.capacityMW == null
    || a.capacityFactorPct == null
    || a.projectLifeYears == null
    || a.waccPct == null
  ) {
    return null;
  }
  if (!hasPhysicalInputs(a)) return null;
  const annualMwh = computeAnnualMwh(a.capacityMW, a.capacityFactorPct);
  if (annualMwh == null || annualMwh <= 0) return null;
  const opex = computeAnnualOpexUsd(a);
  if (opex == null) return null;
  const rate = rateOverrideFraction ?? a.waccPct / 100;
  const lifeYears = effectiveProjectLifeYears(a);
  if (lifeYears == null) return null;
  const crf = capitalRecoveryFactor(rate, lifeYears);
  // EFFECTIVE capital: contingency, land and IDC are part of what has to be
  // raised, so they are part of what the energy has to repay.
  const capex = computeEffectiveCapexUsd(a) ?? a.capexTotalUsd;
  // B14 — O&M escalates across the project life, levelised against the same
  // NOMINAL rate the CAPEX term is discounted at. This keeps the defining
  // property intact: LCOE is still the flat price at which NPV is zero, because
  // the cashflow builder escalates the same opex at the same rate.
  // Fuel and non-fuel escalate on DIFFERENT paths, so each is levelised with
  // its own factor. Summing them is exact: levelisation is linear in the cost,
  // so `L(nonFuel) + L(fuel)` is the levelised total, and the breakeven
  // identity below survives per-component escalators unchanged.
  const fuel = computeAnnualFuelUsd(a) ?? 0;
  const nonFuel = opex - fuel;
  const levelisedOpex =
    nonFuel * opexLevelisationFactor(rate, opexEscalatorFraction, lifeYears)
    + fuel * opexLevelisationFactor(rate, fuelEscalatorFraction, lifeYears);
  let lcoe = (capex * crf + levelisedOpex) / annualMwh;

  // H-5 — when the flag is on, fold in the discounted decommissioning cost and
  // the working-capital lock-up carry cost. Magnitudes come from the explicit
  // inputs when present, else the sourced defaults (financeFlags.ts). Flag OFF
  // ⇒ resolveWcDecommUsd returns {0, 0} so this block adds nothing (byte-
  // identical to today). The WC/decomm bases (year-one revenue / total CAPEX)
  // are the same values the cashflow builder uses below.
  const { workingCapitalUsd, decommissioningUsd } = resolveWcDecommUsd({
    workingCapitalUsd: a.workingCapitalUsd,
    decommissioningUsd: a.decommissioningUsd,
    yearOneRevenueUsd: computeYearOneRevenueUsd(a),
    capexTotalUsd: a.capexTotalUsd,
  });

  // LEVELISATION (fixed 2026-08-19). Every one-off cost must be levelised the
  // SAME way the CAPEX term is: `PV × CRF / annualMwh`. CRF converts a present
  // value into the equivalent LEVEL ANNUAL payment; dividing that annual payment
  // by the annual energy gives $/MWh.
  //
  // The previous code used two different, both-wrong conversions:
  //   decommissioning: PV / annualMwh          — a present value divided by ONE
  //                                              year of energy (≈11× too big
  //                                              at 25 yr / 8.25%)
  //   working capital: PV / (annualMwh × life) — a present value spread over
  //                                              undiscounted lifetime energy
  //                                              (≈0.4× too small)
  // The consequence was an LCOE that no longer equalled the breakeven PPA price
  // — the defining property of the metric. This flag is ON in production
  // (`_FA_WC_DECOMM: 'true'` in cloudbuild.yaml), so every persisted LCOE
  // carried the error.
  const oneOffPvUsd =
    (decommissioningUsd > 0
      ? decommissioningUsd / Math.pow(1 + rate, lifeYears)
      : 0)
    // Working capital is locked up at year 0 and recovered at year N; its cost
    // is the PV difference between the outflow and the discounted recovery.
    + (workingCapitalUsd > 0
      ? workingCapitalUsd - workingCapitalUsd / Math.pow(1 + rate, lifeYears)
      : 0);

  if (oneOffPvUsd > 0) {
    lcoe += (oneOffPvUsd * crf) / annualMwh;
  }

  return lcoe;
}

/**
 * Levelised Cost of Energy in NOMINAL dollars, USD/MWh — the report's
 * contract-pricing figure.
 *
 * Defined by a PROPERTY, not a formula: this is the flat PPA price at which the
 * project's NPV is zero. The cashflow builder escalates O&M at the same rate
 * used here, which is what keeps that identity exact.
 */
export function computeLcoeUsdPerMwh(a: FinancialAssumptionsCore): number | null {
  return lcoeCore(
    a,
    null,
    DEFAULT_INFLATION_PCT_PER_YEAR / 100,
    fuelEscalatorPctPerYear(a.technology) / 100,
  );
}

/**
 * Levelised Cost of Energy in CONSTANT (real) dollars, USD/MWh.
 *
 * WHY BOTH EXIST. `computeLcoeUsdPerMwh` is the NOMINAL figure, and it is
 * defined by a property rather than a formula: it is the flat PPA price at which
 * this project's NPV is zero. That makes it the right number for pricing a
 * contract, and it is what the breakeven tests pin exactly.
 *
 * It is NOT the right number for comparison. NREL's ATB, Lazard, the IEA and
 * this platform's own `finDecision/pathwayLcoe` all quote REAL levelised costs,
 * and a nominal figure sits systematically above them by the inflation wedge
 * over the asset life — roughly a third on a sixty-year plant. Printing the
 * nominal figure beside a real benchmark would manufacture exactly the false
 * discrepancy `lcoeBasis.ts` exists to prevent.
 *
 * This is a RATE SWAP, not a second cost model. The Fisher identity says that
 * escalating O&M at `i` and discounting at `r_nominal` is the same calculation
 * as holding O&M flat and discounting at `r_real`, so passing the deflated rate
 * through the very same function is what guarantees the two figures describe one
 * project rather than two that merely resemble each other.
 *
 * Returns null on exactly the same missing inputs as its nominal sibling.
 */
export function computeLcoeRealUsdPerMwh(a: FinancialAssumptionsCore): number | null {
  if (a.waccPct == null || !Number.isFinite(a.waccPct)) return null;
  // BOTH halves of the identity move together: deflate the rate AND hold costs
  // flat. Deflating the rate alone would leave O&M escalating against a real
  // discount rate — inflation counted once in the costs and once more in the
  // rate — which is a third basis, and the wrong one.
  // Non-fuel opex is flat in real terms by construction (it escalates AT
  // inflation). Fuel is NOT — gas outpaces inflation, so it still grows in real
  // terms and gets the deflated growth rate rather than zero. Treating it as
  // flat here would understate a gas plant's real LCOE.
  return lcoeCore(
    a,
    realDiscountRateFraction(a.waccPct / 100),
    0,
    realEscalatorFraction(fuelEscalatorPctPerYear(a.technology) / 100),
  );
}

/**
 * Annual revenue in USD/year — PPA price × annual generation.
 *
 *   revenue_yr1 = ppaPrice × annualMwh
 *   revenue_yr_t = revenue_yr1 × (1 + escalator)^(t-1)
 *
 * Returns null when PPA price or generation is missing. The escalator
 * is OPTIONAL — falls through to flat-price (0% escalator) when
 * absent.
 */
export function computeYearOneRevenueUsd(a: FinancialAssumptionsCore): number | null {
  if (a.ppaPricePerMwhUsd == null) return null;
  const annualMwh = computeAnnualMwh(a.capacityMW, a.capacityFactorPct);
  if (annualMwh == null) return null;
  return a.ppaPricePerMwhUsd * annualMwh;
}

// ─────────────────────────────────────────────────────────────────────────────
// REVENUE — the PPA term, and what happens after it.
//
// Review 2026-08-19: `ppaTermYears` was an inert field. Revenue ran at the
// ESCALATED PPA price for the whole design life, so the baseline SMR (PPA $90,
// +2%/yr, term 25, life 60) sold power at $290/MWh in year 60 and took 74% of
// its NPV ($645M of $874M) from years 26-60 — an un-contracted tail priced as
// if it were contracted.
//
// DECISION (Q3, approved): after the term the project sells at a FLAT REAL
// merchant price. The default is the PPA price FROZEN at expiry (no further
// escalation) — the most conservative reading that does not invent a market
// view — and `merchantTailPriceUsdPerMwh` overrides it when a view exists.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Revenue price for project year `t` (t = 1 is the first operating year).
 *
 *   t ≤ ppaTermYears : ppaPrice × (1 + escalator)^(t-1)   — contracted
 *   t >  ppaTermYears : the merchant price                 — uncontracted
 *
 * With no `ppaTermYears` the contract is treated as running for the whole life
 * (the pre-2026-08-19 behaviour), so a model that never declared a term is
 * unchanged.
 */
export function revenuePriceForYear(
  a: FinancialAssumptionsCore,
  t: number,
): number | null {
  if (a.ppaPricePerMwhUsd == null || !Number.isFinite(a.ppaPricePerMwhUsd)) return null;
  const escalator = (a.ppaEscalatorPctPerYear ?? 0) / 100;
  const term = a.ppaTermYears;
  const contracted = term == null || !Number.isFinite(term) || term <= 0 || t <= term;
  if (contracted) return a.ppaPricePerMwhUsd * Math.pow(1 + escalator, t - 1);
  return merchantTailPriceUsdPerMwh(a);
}

/**
 * Price per MWh after the PPA expires. Explicit override when the sponsor has a
 * market view; otherwise the contract price FROZEN at expiry — flat in nominal
 * terms, i.e. declining in real terms, which is the conservative default for an
 * uncontracted tail.
 */
export function merchantTailPriceUsdPerMwh(a: FinancialAssumptionsCore): number {
  const override = (a as { merchantTailPriceUsdPerMwh?: number | null }).merchantTailPriceUsdPerMwh;
  if (override != null && Number.isFinite(override) && override >= 0) return override;
  const base = a.ppaPricePerMwhUsd ?? 0;
  const escalator = (a.ppaEscalatorPctPerYear ?? 0) / 100;
  const term = a.ppaTermYears;
  if (term == null || !Number.isFinite(term) || term <= 0) return base;
  // Price in the final contracted year, held flat thereafter.
  return base * Math.pow(1 + escalator, Math.max(0, term - 1));
}

// ─────────────────────────────────────────────────────────────────────────────
// IRA TAX CREDITS (decision Q2, approved).
//
// `itcPct` and `ptcPerMwhUsd` sat on the schema, were printed to the LLM as
// authoritative FINANCING terms, and were read by NO engine — while the report's
// Tax & Commercial section asserted they were "modeled inside the engine NPV &
// LCOE". They now flow through the cashflows:
//   ITC — a one-time credit at the investment year, as a percentage of the
//         EFFECTIVE CAPEX actually raised (not a $/kW proxy).
//   PTC — a per-MWh production credit for the first `PTC_DURATION_YEARS`
//         operating years, escalating with the declared PPA escalator.
// A project declaring neither is unchanged.
// ─────────────────────────────────────────────────────────────────────────────

/** §45Y / §45U production-credit duration — 10 years from placed-in-service. */
export const PTC_DURATION_YEARS = 10;

/** One-time ITC cash value at the investment year (USD). Zero when undeclared. */
export function computeItcCreditUsd(a: FinancialAssumptionsCore): number {
  const pct = a.itcPct;
  if (pct == null || !Number.isFinite(pct) || pct <= 0) return 0;
  const capex = computeEffectiveCapexUsd(a);
  if (capex == null || capex <= 0) return 0;
  return capex * (pct / 100);
}

/** PTC credit for project year `t` (USD). Zero outside the credit window. */
export function computePtcCreditUsdForYear(
  a: FinancialAssumptionsCore,
  t: number,
  annualMwh: number,
): number {
  const rate = a.ptcPerMwhUsd;
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return 0;
  if (t < 1 || t > PTC_DURATION_YEARS) return 0;
  const escalator = (a.ppaEscalatorPctPerYear ?? 0) / 100;
  return rate * Math.pow(1 + escalator, t - 1) * annualMwh;
}

/**
 * Generate the year-by-year cash flows for IRR / NPV. Year 0 is the
 * negative CAPEX outflow; years 1..N carry (revenue − opex).
 *
 * Simplifications (documented so users know what this DOES NOT
 * model when `isIncludeWcAndDecommissioningEnabled()` is false):
 *   - No tax — pre-tax cash flows. ITC/PTC and depreciation are
 *     ignored; FA14 will reintroduce them.
 *   - No debt schedule — the IRR returned is an unlevered project IRR.
 *   - No working-capital or decommissioning flows (unless flag enabled).
 *   - No residual / salvage value at year N.
 *
 * When `isIncludeWcAndDecommissioningEnabled()` is true (R-131-SCORING-A):
 *   - Year 0 outflow includes `workingCapitalUsd` lock-up.
 *   - Year N inflow includes `workingCapitalUsd` recovery.
 *   - Year N outflow includes `decommissioningUsd`.
 *
 * A2 (cost-timing): when `opts.bom` carries BoM lines WITH `costTiming`, the
 * lump-sum year-0 CAPEX is replaced by a real drawdown curve — deposits land
 * `orderLeadMonths` before delivery (which can be BEFORE COD), the balance at
 * delivery / COD. The vector is then re-anchored so index 0 is the EARLIEST
 * outflow year (a pre-COD deposit year), and revenue still starts at COD. When
 * `opts.bom` is absent OR no line carries `costTiming`, behaviour is UNCHANGED
 * (whole CAPEX at year 0) — untimed BoMs are byte-identical to the legacy path.
 * The COD year is read from `a.inServiceDate`; absent ⇒ the drawdown collapses
 * back onto year 0 (no calendar anchor to spread against).
 *
 * Reports lean on the FORMULA being well-defined and dimensionally
 * correct; investment-grade IRR/NPV in the uploaded model's own LCOE
 * remains the source of truth.
 */
export function computeCashFlowsUsd(
  a: FinancialAssumptionsCore,
  opts?: { bom?: readonly BomItem[] | null },
): number[] | null {
  if (a.capexTotalUsd == null || a.projectLifeYears == null) return null;
  if (!hasPhysicalInputs(a)) return null;
  // WHOLE years — the vector is annual, so a fractional life would discount a
  // year the vector does not contain (and CRF would use the fraction while the
  // loop truncated it).
  const lifeYears = effectiveProjectLifeYears(a);
  if (lifeYears == null) return null;
  const yr1Revenue = computeYearOneRevenueUsd(a);
  const opex = computeAnnualOpexUsd(a);
  if (yr1Revenue == null || opex == null) return null;
  const escalator = (a.ppaEscalatorPctPerYear ?? 0) / 100;
  // B14 — O&M escalates on its OWN rate (general inflation), NOT the PPA
  // contract escalator. Revenue escalates because a contract says so; costs
  // escalate because of inflation, and a 0% PPA escalator does not freeze costs
  // for thirty years. Holding opex flat here while escalating revenue and
  // discounting at a nominal WACC understated LCOE ~5% and OVERSTATED NPV by
  // ~7% of CAPEX.
  const opexEscalator = DEFAULT_INFLATION_PCT_PER_YEAR / 100;
  // FUEL ESCALATES ON ITS OWN PATH. Gas tracks the EIA AEO commodity
  // trajectory, not CPI, and for a gas plant fuel is the majority of opex — so
  // escalating it at general inflation understated the lifetime cost
  // materially. This split MUST mirror the one in `lcoeCore`, or LCOE stops
  // being the price at which these cashflows break even.
  const fuelEscalator = fuelEscalatorPctPerYear(a.technology) / 100;
  const annualFuel = computeAnnualFuelUsd(a) ?? 0;
  const annualNonFuel = opex - annualFuel;
  const opexForYear = (t: number): number =>
    annualNonFuel * Math.pow(1 + opexEscalator, t - 1)
    + annualFuel * Math.pow(1 + fuelEscalator, t - 1);
  // H-5 — WC lock-up + decommissioning magnitudes. Flag OFF ⇒ {0, 0} (the
  // legacy no-WC/no-decomm cashflows, byte-identical). Flag ON ⇒ explicit
  // inputs win, else the sourced defaults from financeFlags.ts.
  const { workingCapitalUsd, decommissioningUsd } = resolveWcDecommUsd({
    workingCapitalUsd: a.workingCapitalUsd,
    decommissioningUsd: a.decommissioningUsd,
    yearOneRevenueUsd: yr1Revenue,
    capexTotalUsd: a.capexTotalUsd,
  });

  // A2 — does any BoM line carry cost-timing? Only then do we depart from the
  // legacy lump-sum-at-year-0 layout. The COD year anchors the drawdown.
  const bom = opts?.bom ?? null;
  const codYear = isoYear(a.inServiceDate);
  const hasTiming =
    bom != null && codYear != null && bom.some((it) => it.costTiming?.depositBasis != null);

  if (hasTiming && bom != null && codYear != null) {
    // Per-year CAPEX outflows (positive USD), keyed by calendar year. Total
    // reconciles to Σ priced totalCostUsd by construction (deposit + balance).
    const drawdown = buildCapexDrawdownByYear(bom, codYear);
    // Earliest outflow year — a pre-COD deposit makes this < codYear. The
    // vector starts here so index 0 stays the first (undiscounted) outflow.
    let earliestYear = codYear;
    for (const yr of drawdown.keys()) earliestYear = Math.min(earliestYear, yr);
    // Legacy semantics: operating flows run COD+1 .. COD+projectLifeYears
    // (year 0 = COD is CAPEX-only). The last operating year is therefore
    // `codYear + projectLifeYears`, NOT `… − 1`.
    const lastYear = codYear + lifeYears;
    const numYears = lastYear - earliestYear + 1;
    const flows = new Array<number>(numYears).fill(0);

    // CAPEX outflows (negative) at their drawdown years.
    for (const [yr, usd] of drawdown.entries()) {
      const idx = yr - earliestYear;
      if (idx >= 0 && idx < numYears) flows[idx] -= usd;
    }
    // Working-capital lock-up sits with the first outflow (index 0).
    flows[0] -= workingCapitalUsd;

    // Revenue − opex for project year t lands at calendar year codYear + t,
    // i.e. vector index codIdx + t (so t=1 is the year AFTER COD, matching the
    // legacy layout where index 0 = COD = CAPEX-only).
    const codIdx = codYear - earliestYear;
    // ITC lands at the investment (COD) year, alongside the capital.
    if (codIdx >= 0 && codIdx < numYears) flows[codIdx] += computeItcCreditUsd(a);
    const annualMwhTimed = computeAnnualMwh(a.capacityMW, a.capacityFactorPct) ?? 0;
    for (let t = 1; t <= lifeYears; t += 1) {
      const price = revenuePriceForYear(a, t) ?? 0;
      const revenue = price * annualMwhTimed;
      let flow = revenue - opexForYear(t) + computePtcCreditUsdForYear(a, t, annualMwhTimed);
      if (t === lifeYears) {
        flow += workingCapitalUsd;
        flow -= decommissioningUsd;
      }
      const idx = codIdx + t;
      if (idx >= 0 && idx < numYears) flows[idx] += flow;
    }
    return flows;
  }

  // Legacy path — whole CAPEX at year 0 (untimed / no-BoM behaviour, unchanged).
  // EFFECTIVE capital (contingency + land + IDC), and the one-time ITC credit
  // against it. Year 0 is the investment year: CAPEX out, working capital
  // locked up, ITC received.
  const effectiveCapex = computeEffectiveCapexUsd(a) ?? a.capexTotalUsd;
  const itcCredit = computeItcCreditUsd(a);
  const flows: number[] = [-(effectiveCapex + workingCapitalUsd) + itcCredit];
  const annualMwh = computeAnnualMwh(a.capacityMW, a.capacityFactorPct) ?? 0;
  for (let t = 1; t <= lifeYears; t += 1) {
    // Contracted price through the PPA term, merchant thereafter.
    const price = revenuePriceForYear(a, t) ?? 0;
    const revenue = price * annualMwh;
    let flow = revenue - opexForYear(t) + computePtcCreditUsdForYear(a, t, annualMwh);
    if (t === lifeYears) {
      flow += workingCapitalUsd;
      flow -= decommissioningUsd;
    }
    flows.push(flow);
  }
  return flows;
}

/**
 * Net Present Value at the WACC discount rate (USD).
 *
 *   NPV = Σ_t cashFlow_t / (1 + r)^t
 *
 * Returns null when the cash-flow stream or discount rate is
 * unavailable.
 */
export function computeNpvUsd(a: FinancialAssumptionsCore): number | null {
  if (a.waccPct == null) return null;
  const flows = computeCashFlowsUsd(a);
  if (!flows) return null;
  return discountedSum(flows, a.waccPct / 100);
}

/**
 * Pure discounted sum — used by NPV and IRR. Year-0 flow is NOT
 * discounted (the CAPEX is paid today). Year-t flow is divided by
 * (1 + r)^t.
 */
export function discountedSum(flows: number[], rate: number, baseIndex = 0): number {
  let total = 0;
  for (let t = 0; t < flows.length; t += 1) {
    // Exponent is measured FROM THE BASE YEAR. A flow before the base year gets
    // a negative exponent and is therefore COMPOUNDED FORWARD — money spent two
    // years before the valuation date is worth more at that date, not less.
    //
    // This matters for the phased engine, whose vector can start at a pre-COD
    // deposit year: discounting to index 0 would silently re-base the NPV
    // whenever a scenario or a BoM change moved the earliest outflow, so two
    // NPVs for the same project were not comparable.
    total += flows[t] / Math.pow(1 + rate, t - baseIndex);
  }
  return total;
}

/**
 * Solve `discountedSum(flows, r, baseIndex) = 0` for r — Newton-Raphson with a
 * bisection fallback. Returns the rate as a FRACTION (0.124 = 12.4%), or null
 * when the flows do not support an IRR (one-sided, or no convergence).
 *
 * THE shared solver: the phased engine used to carry its own copy WITHOUT the
 * bisection fallback, so a phased project whose Newton iteration diverged got a
 * silent null and the stamp fell back to the single-phase IRR — two different
 * computations reported under one label.
 *
 * The base year does not change the ROOT (scaling every term by a constant
 * `(1+r)^baseIndex` cannot move a zero), so it is accepted only to keep the
 * solver's objective identical to the NPV the caller reports.
 */
export function solveIrrFraction(flows: number[], baseIndex = 0): number | null {
  if (!flows.some(f => f < 0) || !flows.some(f => f > 0)) return null;

  let r = 0.10;
  const MAX_ITERS = 100;
  const TOL_NPV = 1e-3; // USD — tight for typical CAPEX magnitudes
  const TOL_RATE = 1e-9;
  for (let iter = 0; iter < MAX_ITERS; iter += 1) {
    const npv = discountedSum(flows, r, baseIndex);
    if (Math.abs(npv) < TOL_NPV) return r;
    // dNPV/dr = − Σ (t − base) × CF_t / (1+r)^(t−base+1)
    let derivative = 0;
    for (let t = 0; t < flows.length; t += 1) {
      const n = t - baseIndex;
      if (n === 0) continue;
      derivative += -n * flows[t] / Math.pow(1 + r, n + 1);
    }
    if (Math.abs(derivative) < 1e-12) break;
    const step = npv / derivative;
    const next = r - step;
    if (Math.abs(step) < TOL_RATE) return next;
    if (next <= -0.99) r = -0.5;
    else if (next > 100) break; // > 10,000% is degenerate — hand to bisection
    else r = next;
  }
  return bisectIrr(flows, -0.99, 5.0, baseIndex);
}

// ─────────────────────────────────────────────────────────────────────────────
// A2 — CAPEX cost-timing drawdown (Wave A keystone).
//
// WHY: lumping each phase's whole CAPEX in its COD year overstates the present
// value of the spend — long-lead equipment (SMR modules, GSUs, turbines) is
// ORDERED years before COD and a deposit leaves the account on order, while the
// balance lands at delivery or is held to COD. Discounting the deposit at the
// (earlier) order year vs the (later) COD year materially moves NPV / IRR /
// payback for staged builds (Holly Ridge + Redhead). This helper converts a set
// of BoM lines + their `costTiming` into a year-keyed outflow map so Finance can
// build the real drawdown curve. Lines WITHOUT `costTiming` preserve today's
// behaviour: the whole cost lands on its delivery date (default = phase COD).
// ─────────────────────────────────────────────────────────────────────────────

/** Months in a year — named per CLAUDE.md §4 (no magic numbers). */
const MONTHS_PER_YEAR = 12;

/**
 * Parse a 4-digit year from an ISO date (YYYY-MM-DD or full timestamp). Returns
 * null when absent / unparseable so callers can fall back to a phase anchor.
 */
function isoYear(iso: string | null | undefined): number | null {
  if (!iso || iso.length < 4) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

/**
 * Resolve the DELIVERY year for one BoM line. Prefers the line's own
 * `requiredOnSiteDate`, then `expectedOnSiteDate`; falls back to the supplied
 * phase COD year when the line carries no date (so an untimed/undated line
 * still lands somewhere sensible). MWh / capacity are irrelevant here — this is
 * purely a SCHEDULE resolution.
 */
function deliveryYearForLine(item: BomItem, phaseCodYear: number): number {
  return (
    isoYear(item.requiredOnSiteDate)
    ?? isoYear(item.expectedOnSiteDate)
    ?? phaseCodYear
  );
}

/**
 * A2 — accumulate one BoM line's cash events into a year→outflow map (USD,
 * POSITIVE magnitudes — the caller decides the sign). The map key is the
 * calendar year of each event.
 *
 *   - No `costTiming` (legacy untimed line): the whole `totalCostUsd` lands on
 *     the line's delivery year (requiredOnSite / expectedOnSite / phase COD).
 *     This is byte-identical to the pre-A2 "whole cost at COD" behaviour when
 *     the line has no on-site dates.
 *   - With `costTiming`:
 *       deposit  = bomItemDepositUsd(item), paid `orderLeadMonths` BEFORE the
 *                  delivery year (rounded to whole years; 0 lead ⇒ delivery yr).
 *       balance  = totalCostUsd − deposit, paid at delivery (`at-delivery`) or
 *                  held to COD (`at-cod`).
 *
 * Unpriced lines (`totalCostUsd` null/non-finite) contribute nothing — an
 * unpriced line has no cash to draw, consistent with `itemizedCapexFromBom`.
 * NaN / negative guarded throughout: a negative balance (deposit clamped to
 * totalCost upstream guarantees this can't happen, but we floor anyway) never
 * books a negative outflow.
 */
function accumulateLineDrawdown(
  item: BomItem,
  phaseCodYear: number,
  out: Map<number, number>,
): void {
  const total = item.totalCostUsd;
  if (total == null || !Number.isFinite(total) || total <= 0) return;

  const deliveryYear = deliveryYearForLine(item, phaseCodYear);
  const timing = item.costTiming;

  // Legacy untimed line — whole cost on the delivery year.
  if (!timing || !timing.depositBasis) {
    out.set(deliveryYear, (out.get(deliveryYear) ?? 0) + total);
    return;
  }

  const deposit = bomItemDepositUsd(item); // already clamped to [0, total]
  const balance = Math.max(0, total - deposit);

  // Deposit lands `orderLeadMonths` before delivery. Whole-year resolution: we
  // floor lead-months to whole years so the deposit lands in a discrete
  // cashflow-vector year. Round-up of a partial year would over-discount; floor
  // keeps the deposit no EARLIER than the true order date.
  if (deposit > 0) {
    const leadMonths =
      timing.orderLeadMonths != null && Number.isFinite(timing.orderLeadMonths) && timing.orderLeadMonths > 0
        ? timing.orderLeadMonths
        : 0;
    const leadYears = Math.floor(leadMonths / MONTHS_PER_YEAR);
    const depositYear = deliveryYear - leadYears;
    out.set(depositYear, (out.get(depositYear) ?? 0) + deposit);
  }

  // Balance: at delivery (default) or held to COD.
  if (balance > 0) {
    const balanceYear = timing.balanceTiming === 'at-cod' ? phaseCodYear : deliveryYear;
    out.set(balanceYear, (out.get(balanceYear) ?? 0) + balance);
  }
}

/**
 * A2 — public entry: build a year→CAPEX-outflow map (POSITIVE USD magnitudes)
 * for a list of BoM lines anchored to a phase's COD year. Pure; never throws.
 *
 * The returned map's TOTAL across all years equals Σ priced `totalCostUsd`
 * (deposit + balance reconciles to the line cost), so cost-timing only
 * REDISTRIBUTES spend across years — it never creates or destroys CAPEX. This
 * conservation property is asserted by the deposit-timing test.
 *
 * @param items        BoM lines to draw down (already attributed to the phase).
 * @param phaseCodYear The phase's COD year — the anchor for undated lines + the
 *                     `at-cod` balance, and the reference for `orderLeadMonths`.
 */
export function buildCapexDrawdownByYear(
  items: readonly BomItem[],
  phaseCodYear: number,
): Map<number, number> {
  const out = new Map<number, number>();
  if (!Array.isArray(items) || !Number.isFinite(phaseCodYear)) return out;
  for (const item of items) accumulateLineDrawdown(item, phaseCodYear, out);
  return out;
}

/**
 * Project IRR via Newton-Raphson on `discountedSum(flows, r) = 0`.
 *
 * Returns the IRR as a percentage (e.g. 12.4 for 12.4%), or null when
 * the cash flows don't support an IRR (e.g. all positive — never
 * crosses zero; or all negative — same).
 *
 * Convergence:
 *   - Initial guess r = 0.10 (10%) — a sane mid-range for utility-
 *     scale projects.
 *   - Tolerance 1e-7 on NPV, 1e-9 on rate-step.
 *   - Cap at 100 iterations. Falls through to bisection if Newton
 *     diverges (e.g. derivative near zero on a flat NPV-vs-rate
 *     curve).
 *
 * Returns null when:
 *   - cash flows missing or one-sided (all + or all -);
 *   - rate goes negative below -99% (degenerate);
 *   - no convergence after the iteration cap.
 */
export function computeProjectIrrPct(a: FinancialAssumptionsCore): number | null {
  const flows = computeCashFlowsUsd(a);
  if (!flows) return null;
  const r = solveIrrFraction(flows);
  return r == null ? null : r * 100;
}

function bisectIrr(flows: number[], lo: number, hi: number, baseIndex = 0): number | null {
  let fLo = discountedSum(flows, lo, baseIndex);
  let fHi = discountedSum(flows, hi, baseIndex);
  if (Number.isNaN(fLo) || Number.isNaN(fHi)) return null;
  if (fLo * fHi > 0) return null;
  for (let iter = 0; iter < 200; iter += 1) {
    const mid = 0.5 * (lo + hi);
    const fMid = discountedSum(flows, mid, baseIndex);
    if (Math.abs(fMid) < 1e-3 || Math.abs(hi - lo) < 1e-9) return mid;
    if (fMid * fLo < 0) {
      hi = mid;
      fHi = fMid;
    } else {
      lo = mid;
      fLo = fMid;
    }
  }
  return 0.5 * (lo + hi);
}

/**
 * Simple payback period in years — the first year at which the
 * cumulative undiscounted cash flow turns positive. Linear-
 * interpolates within the crossing year for sub-year precision.
 *
 * Returns null when the project never pays back (cumulative stays
 * negative through year N) or when cash flows are unavailable.
 */
export function computePaybackYears(a: FinancialAssumptionsCore): number | null {
  const flows = computeCashFlowsUsd(a);
  if (!flows) return null;
  let cumulative = 0;
  for (let t = 0; t < flows.length; t += 1) {
    const prevCumulative = cumulative;
    cumulative += flows[t];
    if (prevCumulative < 0 && cumulative >= 0 && t > 0) {
      // Linear interpolation: payback within year t.
      const fraction = -prevCumulative / flows[t];
      return (t - 1) + fraction;
    }
  }
  return null;
}

/**
 * Derive capexPerKwUsd from capexTotalUsd + capacityMW (or null).
 * The form lights up this field automatically when both inputs are
 * present; user-edits flip the provenance chip from `derived` to
 * `manual` and stop the auto-derivation.
 */
export function deriveCapexPerKw(capexTotalUsd: number | null, capacityMW: number | null): number | null {
  if (capexTotalUsd == null || capacityMW == null) return null;
  if (capacityMW <= 0) return null;
  return capexTotalUsd / (capacityMW * KW_PER_MW);
}

/**
 * Derive WACC from D:E ratio + debt rate + equity hurdle + tax rate
 * (after-tax WACC). Returns null when any input is missing.
 *
 *   WACC = D × r_d × (1 - t) + (1 - D) × r_e
 *   where D = debtToEquityRatio (in [0, 1]) — debt fraction
 */
export function deriveWaccPct(
  debtToEquityRatio: number | null,
  debtRatePct: number | null,
  equityHurdlePct: number | null,
  taxRatePct: number | null,
): number | null {
  if (
    debtToEquityRatio == null
    || debtRatePct == null
    || equityHurdlePct == null
    || taxRatePct == null
  ) {
    return null;
  }
  if (debtToEquityRatio < 0 || debtToEquityRatio > 1) return null;
  const taxFraction = taxRatePct / 100;
  const debtCost = debtToEquityRatio * (debtRatePct / 100) * (1 - taxFraction);
  const equityCost = (1 - debtToEquityRatio) * (equityHurdlePct / 100);
  return (debtCost + equityCost) * 100;
}

/**
 * Output of the recompute driver — the four output metrics + the
 * derived helper fields. UI binds to this and decides whether to
 * show "derived" provenance chips next to each field.
 */
export interface RecomputeResult {
  /** The contract-pricing figure: the flat PPA price at which NPV is zero. */
  lcoeNominalPerMwhUsd: number | null;
  /**
   * The COMPARABLE figure, in constant dollars. Use this against NREL ATB,
   * Lazard, or this platform's own benchmark pathways — all of which quote
   * real levelised costs. Comparing the nominal figure to any of them
   * manufactures a discrepancy worth roughly the inflation wedge over the
   * asset life.
   */
  lcoeRealPerMwhUsd: number | null;
  npvUsd: number | null;
  projectIrrPct: number | null;
  paybackYears: number | null;
  /** Derived helper — capex per kW when both CAPEX and capacity are set. */
  capexPerKwUsd: number | null;
  /** Derived helper — after-tax WACC when D:E + rates + tax are set. */
  waccPct: number | null;
  /** Convenience — annual energy in MWh; used by the chart strip. */
  annualMwh: number | null;
}

/**
 * Pure recompute driver — given a FinancialAssumptionsCore (or full
 * FinancialAssumptions), return the four output metrics plus the two
 * derived helper fields and annual energy. All `null` when inputs
 * aren't sufficient.
 *
 * Critical: this does NOT mutate the input. The form's reducer is
 * responsible for choosing whether to write the recomputed values
 * back into `assumptions.*` (with `derived` provenance) or leave them
 * as a "would compute to X" sanity-check chip.
 */
export function recompute(a: FinancialAssumptionsCore): RecomputeResult {
  // Fail closed for a missed dispatch. DC grid consumption is not generation.
  if ('businessModelKind' in a && a.businessModelKind === 'data-center-capacity-fee') {
    return { lcoeNominalPerMwhUsd: null, lcoeRealPerMwhUsd: null, npvUsd: null,
      projectIrrPct: null, paybackYears: null, capexPerKwUsd: null, waccPct: null, annualMwh: null };
  }
  // Light-touch derivation: if WACC isn't explicitly set, fall back
  // to the derived WACC. Mirror the UX where the form auto-fills the
  // WACC field. Note the input is treated as read-only — we build a
  // shallow clone with the derived WACC swapped in for the LCOE/NPV
  // formula to consume.
  const derivedWacc = deriveWaccPct(
    a.debtToEquityRatio,
    a.debtRatePct,
    a.equityHurdlePct,
    a.taxRatePct,
  );
  const derivedCapexPerKw = deriveCapexPerKw(a.capexTotalUsd, a.capacityMW);
  const effective: FinancialAssumptionsCore = {
    ...a,
    waccPct: a.waccPct ?? derivedWacc,
    capexPerKwUsd: a.capexPerKwUsd ?? derivedCapexPerKw,
  };
  return {
    lcoeNominalPerMwhUsd: computeLcoeUsdPerMwh(effective),
    lcoeRealPerMwhUsd: computeLcoeRealUsdPerMwh(effective),
    npvUsd: computeNpvUsd(effective),
    projectIrrPct: computeProjectIrrPct(effective),
    paybackYears: computePaybackYears(effective),
    capexPerKwUsd: derivedCapexPerKw,
    waccPct: derivedWacc,
    annualMwh: computeAnnualMwh(effective.capacityMW, effective.capacityFactorPct),
  };
}

